import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * FAR Print Shop Header JS v1.0 Stable Click-Only Owner Fix
 *
 * File: assets/header.js
 *
 * Purpose:
 * - Preserve Shopify-native Balance/Horizon header behavior.
 * - Preserve sticky header, header height calculation, header group height, overflow menu switching, and hydration.
 * - Add stable desktop click-only dropdown behavior at the actual header JS owner level.
 * - Prevent native hover/mouseleave dropdown closing after a click-open state.
 * - Do not use setInterval.
 * - Do not use MutationObserver for the click menu.
 * - Do not touch mobile drawer, search, cart, account, or predictive search behavior.
 */

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when it is actively sticky.
   * @param {boolean} alwaysSticky - Determines if we need to observe when the header is offscreen.
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu.
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer.
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer.
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }

    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/**
 * FAR desktop click-only dropdown controller.
 *
 * This intentionally lives in assets/header.js because the native header/menu behavior
 * was closing the dropdown on pointer exit after section-level scripts opened it.
 */
function initFarClickOnlyHeaderMenu() {
  const header = document.querySelector('header-component');

  if (!header || header.dataset.farClickOnlyOwnerBooted === 'true') return;

  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const headerMenus = Array.from(header.querySelectorAll('header-menu'));

  if (!headerMenus.length) return;

  header.dataset.farClickOnlyOwnerBooted = 'true';
  header.dataset.farClickMenu = 'true';

  let openItem = null;
  let intentionalClose = false;

  const isDesktop = () => desktopQuery.matches;

  const cssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const getMenuScope = (target) => {
    if (!(target instanceof Element)) return null;
    return target.closest('header-menu');
  };

  const findMenuItem = (trigger) => {
    if (!(trigger instanceof Element)) return null;
    return trigger.closest('details, .menu-list__list-item, li');
  };

  const findControlledPanel = (trigger) => {
    if (!(trigger instanceof Element)) return null;

    const controlledId = trigger.getAttribute('aria-controls');
    if (!controlledId) return null;

    return (
      document.getElementById(controlledId) ||
      header.querySelector(`#${cssEscape(controlledId)}`)
    );
  };

  const findSubmenu = (item, trigger) => {
    if (!(item instanceof Element)) return null;

    return (
      item.querySelector(
        '.menu-list__submenu, .mega-menu, .mega-menu__list, [data-menu-panel], [popover]'
      ) || findControlledPanel(trigger)
    );
  };

  const getTrigger = (item) => {
    if (!(item instanceof Element)) return null;

    return item.querySelector(
      ':scope > summary .menu-list__link, :scope > summary, :scope > .menu-list__link, :scope > a, :scope > button, .menu-list__link[aria-haspopup], a[aria-haspopup], button[aria-haspopup]'
    );
  };

  const itemHasDropdown = (item, trigger) => {
    if (!(item instanceof Element)) return false;

    if (item.matches('details')) return true;
    if (findSubmenu(item, trigger)) return true;
    if (findControlledPanel(trigger)) return true;

    const ariaHasPopup = trigger?.getAttribute?.('aria-haspopup');
    if (ariaHasPopup && ariaHasPopup !== 'false') return true;

    return false;
  };

  const setHeaderOpenHeight = (item) => {
    if (!(item instanceof Element)) {
      header.style.removeProperty('--submenu-height');
      header.style.removeProperty('--full-open-header-height');
      header.removeAttribute('data-far-menu-open');
      return;
    }

    const trigger = getTrigger(item);
    const panel = findSubmenu(item, trigger);
    const panelHeight = panel
      ? Math.ceil(panel.scrollHeight || panel.getBoundingClientRect().height || 0)
      : 0;
    const headerRow = header.querySelector('.header__row');
    const headerHeight = Math.ceil(headerRow?.getBoundingClientRect().height || 60);

    if (panelHeight > 0) {
      header.style.setProperty('--submenu-height', `${panelHeight}px`);
      header.style.setProperty('--full-open-header-height', `${headerHeight + panelHeight}px`);
      header.dataset.farMenuOpen = 'true';
    } else {
      header.style.removeProperty('--submenu-height');
      header.style.removeProperty('--full-open-header-height');
      header.removeAttribute('data-far-menu-open');
    }
  };

  const setExpanded = (item, expanded) => {
    if (!(item instanceof Element)) return;

    const trigger = getTrigger(item);
    const panel = findSubmenu(item, trigger);

    if (item.matches('details')) {
      item.open = expanded;
    }

    if (expanded) {
      item.setAttribute('data-far-click-open', 'true');
    } else {
      item.removeAttribute('data-far-click-open');
    }

    if (trigger) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    if (panel) {
      if (expanded) {
        panel.setAttribute('data-far-click-open', 'true');
        panel.removeAttribute('hidden');
        panel.style.removeProperty('display');
        panel.style.removeProperty('visibility');
        panel.style.removeProperty('opacity');
        panel.style.removeProperty('pointer-events');
      } else {
        panel.removeAttribute('data-far-click-open');
      }
    }
  };

  const closeMenu = () => {
    if (!openItem) return;

    intentionalClose = true;
    setExpanded(openItem, false);
    intentionalClose = false;

    openItem = null;
    setHeaderOpenHeight(null);
  };

  const openMenu = (item) => {
    if (!(item instanceof Element)) return;

    if (openItem && openItem !== item) {
      intentionalClose = true;
      setExpanded(openItem, false);
      intentionalClose = false;
    }

    openItem = item;
    setExpanded(item, true);

    window.requestAnimationFrame(() => {
      if (!openItem || openItem !== item) return;
      setExpanded(item, true);
      setHeaderOpenHeight(item);
    });
  };

  const shouldProtectPointerExit = (event) => {
    if (!openItem || !isDesktop()) return false;

    const target = event.target;
    if (!(target instanceof Element)) return false;

    if (target === openItem || openItem.contains(target) || target.closest('header-menu')) {
      return true;
    }

    return false;
  };

  const protectPointerExit = (event) => {
    if (!shouldProtectPointerExit(event)) return;

    event.preventDefault();
    event.stopPropagation();

    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    if (openItem) {
      setExpanded(openItem, true);
      setHeaderOpenHeight(openItem);
    }
  };

  const handleToggle = (event) => {
    if (!openItem || intentionalClose || !isDesktop()) return;

    const target = event.target;
    if (!(target instanceof HTMLDetailsElement)) return;
    if (target !== openItem) return;

    if (!target.open) {
      window.requestAnimationFrame(() => {
        if (!openItem || openItem !== target || intentionalClose || !isDesktop()) return;
        setExpanded(target, true);
        setHeaderOpenHeight(target);
      });
    }
  };

  const handleHeaderClick = (event) => {
    if (!isDesktop()) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('.menu-list__link, summary, a, button');
    if (!(trigger instanceof Element)) return;
    if (!header.contains(trigger)) return;
    if (!getMenuScope(trigger)) return;

    const item = findMenuItem(trigger);
    if (!item || !itemHasDropdown(item, trigger)) {
      if (openItem && trigger.matches('a[href]')) closeMenu();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    if (openItem === item) {
      closeMenu();
      return;
    }

    openMenu(item);
  };

  const handleDocumentClick = (event) => {
    if (!openItem) return;

    const target = event.target;
    if (target instanceof Element && header.contains(target)) return;

    closeMenu();
  };

  const handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    closeMenu();
  };

  const handleBreakpointChange = () => {
    if (!isDesktop()) closeMenu();
  };

  header.addEventListener('click', handleHeaderClick, true);

  document.addEventListener('click', handleDocumentClick, true);
  document.addEventListener('keydown', handleKeydown);

  /**
   * Capture these before native theme menu listeners close details on pointer exit.
   * This is event-based, not observer-based or interval-based.
   */
  document.addEventListener('mouseleave', protectPointerExit, true);
  document.addEventListener('mouseout', protectPointerExit, true);
  document.addEventListener('pointerleave', protectPointerExit, true);
  document.addEventListener('pointerout', protectPointerExit, true);

  header.addEventListener('toggle', handleToggle, true);

  if (typeof desktopQuery.addEventListener === 'function') {
    desktopQuery.addEventListener('change', handleBreakpointChange);
  } else if (typeof desktopQuery.addListener === 'function') {
    desktopQuery.addListener(handleBreakpointChange);
  }
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  initFarClickOnlyHeaderMenu();

  /**
   * Initial header heights are set via inline script in theme.liquid.
   * This ResizeObserver handles dynamic updates after page load.
   */
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header?.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }

        return totalHeight;
      }, 0);

      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];

      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    /**
     * Shopify-native header group observer.
     * This is not used for FAR dropdown behavior.
     */
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;

        const updatedChildren = headerGroup.children;
        for (let i = 0; i < updatedChildren.length; i++) {
          const element = updatedChildren[i];

          if (element instanceof HTMLElement) {
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});